<script lang="ts" setup>
import InfoCard from '@/home/components/InfoCard.vue';
import useMarkdown from '@/home/hooks/markdown';
import { useAsyncData } from '@/home/hooks/async-data';
import { $fetch } from '@/home/plugins/fetch';

const marked = useMarkdown();

const { data: summaryList } = useAsyncData(
  'law:lecture:summary',
  () => $fetch('/law/resource/v1/list', {
    data: {
      category: import.meta.env.VITE_LAW_LECTURE_CATEGORY_ID1,
      limit: -1,
    },
  }),
);

const { data: courseSummaryList } = useAsyncData(
  'law:lecture:courseSummary',
  () => $fetch('/law/resource/v1/list', {
    data: {
      category: import.meta.env.VITE_LAW_LECTURE_CATEGORY_ID2,
      limit: -1,
    },
  }),
);

const { data: lectureList } = useAsyncData<{
  category: string;
  categoryName: string;
  resourceList: {
    title: string;
    content: string;
    cover: string;
    url: string;
  }[];
}[]>(
  'law:lecture:list',
  () => $fetch('/law/resource/v1/course', { data: { limit: -1 } }),
)
</script>

<template>
  <div class="page-law-lectures color-hex-2d2d2d">
    <template v-if="summaryList?.length">
      <div class="header w-full p-32px" v-for="item in summaryList" :key="item.title">
        <h2 class="m-0 text-32px color-hex-1180ff font-medium leading-48px">{{ item.title }}</h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="paragraph m-0 mt-16px" v-html="marked.parse(item.content)"></div>
      </div>
    </template>

    <div class="paragraph mb-48px mt-48px">
      <h2 class="m-0 text-center text-32px font-500 leading-48px">{{ $t('law.lectures.title2') }}</h2>
      <div class="mt-32px h-336px flex flex-wrap content-between justify-between <2xl:h-384px">
        <InfoCard v-for="item in courseSummaryList" :key="item.title" :title="item.title" class="h-152px w-664px <2xl:h-176px <2xl:w-584px">
          <span class="inline-block h-50px w-600px overflow-y-auto text-16px leading-24px <2xl:h-72px <2xl:w-520px">
            {{ item.content }}
          </span>
        </InfoCard>
      </div>
    </div>

    <div v-for="item in lectureList" :key="item.category">
      <div class="video-card mt-48px box-border">
        <h2 class="m-0 mb-32px text-center text-32px font-medium leading-48px">{{ item.categoryName }}</h2>

        <ul class="m-0 flex flex-wrap p-0">
          <li class="group w-432px list-none <2xl:w-378px" v-for="(item1, index1) in item.resourceList" :key="index1" :class="[(index1 + 1) % 3 ? 'mr-[32px]' : '', (index1 - 2) > 0 ? 'mt-32px' : '']">
            <a :href="item1.url" target="_blank" class="block color-hex-000 no-underline">
              <div class="relative h-220px w-full flex-shrink-0 cursor-pointer truncate after:absolute after:top-0px after:h-220px after:w-full after:bg-[rgba(45,45,45,0.2)] after:opacity-100 after:transition-opacity after:transition-duration-300 after:content-empty after:group-hover:opacity-0 <2xl:h-200px">
                <img class="lazyload block h-full w-full border-hex-979797 rounded-4px object-cover transition-all transition-duration-300 group-hover:translate--0.5% group-hover:scale-105" :data-src="item1.cover" :alt="item1.title">
              </div>
              <span class="my-8px block block w-full truncate text-24px font-medium leading-36px group-hover:text-brand-primary" :title="item1.title">
                {{ item1.title }}
              </span>
              <p class="line-clamp-2 m-0px w-full text-16px color-hex-9A9EA6 leading-24px" :title="item1.content">
                {{ item1.content }}
              </p>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.paragraph {
  &:deep() {
    p {
      @apply my-0 not-first:mt-24px text-16px font-normal leading-24px;
    }

    a {
      color: #1180ff;
      text-decoration: none;
      list-style: none;
    }
  }
}

.header {
  box-sizing: border-box;
  background: url(https://res.oafimg.cn/-/42ac9c90e8cd94e8/2.png) no-repeat;
  background-color: #fcfcfc;
  background-position: 862px 16px;
  background-size: 482px 241px;
}

@screen lt-2xl {
  .header {
    box-sizing: border-box;
    background: url(https://res.oafimg.cn/-/42ac9c90e8cd94e8/2.png) no-repeat;
    background-color: #fcfcfc;
    background-position: 702px 16px;
    background-size: 482px 241px;
  }
}
</style>
